import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/Layout/MainLayout';
import { ArrowLeft, MapPin, Home, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UserServiceData {
  buildingName: string;
  buildingAddress: string;
  apartmentNumber: string;
  meterId: string;
  status: string;
  organizationName: string;
}

const Address: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [serviceData, setServiceData] = useState<UserServiceData | null>(null);

  useEffect(() => {
    fetchUserServiceData();
  }, []);

  const fetchUserServiceData = async () => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      // Fetch user data with apartment and building info
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select(`
          id,
          status,
          organization_id,
          user_apartments (
            apartments (
              id,
              apartment_number,
              meter_id,
              occupancy_status,
              buildings (
                id,
                name,
                address
              )
            )
          )
        `)
        .eq('id', user.id)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
        throw userError;
      }

      // Get organization name
      let orgName = 'Growth Energy';
      if (userData?.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('name')
          .eq('id', userData.organization_id)
          .single();
        if (orgData?.name) {
          orgName = orgData.name;
        }
      }

      // Extract apartment and building info
      const apartment = userData?.user_apartments?.[0]?.apartments;
      const building = apartment?.buildings;

      setServiceData({
        buildingName: building?.name || 'Not assigned',
        buildingAddress: building?.address || 'No address on file',
        apartmentNumber: apartment?.apartment_number || 'N/A',
        meterId: apartment?.meter_id || 'No meter assigned',
        status: userData?.status || 'active',
        organizationName: orgName,
      });

    } catch (error) {
      console.error('Error loading service data:', error);
      toast({
        title: "Error",
        description: "Failed to load service address data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout title="Service Address">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return <MainLayout title="Service Address">
      <div className="p-4 space-y-6">
        <button onClick={() => navigate('/settings')} className="flex items-center space-x-2 text-blue-500 hover:text-blue-600">
          <ArrowLeft size={20} />
          <span>Back to Settings</span>
        </button>

        {/* Current Address */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Service Address</h3>
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center mt-1">
                <Home size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">{serviceData?.buildingName}</p>
                <p className="text-gray-600 dark:text-gray-400">Apartment {serviceData?.apartmentNumber}</p>
                <p className="text-gray-600 dark:text-gray-400">{serviceData?.buildingAddress}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Service Information</h3>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Provider:</span>
              <span className="font-medium text-gray-900 dark:text-white">{serviceData?.organizationName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Meter ID:</span>
              <span className="font-medium font-mono text-gray-900 dark:text-white">{serviceData?.meterId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Service Type:</span>
              <span className="font-medium text-gray-900 dark:text-white">Prepaid Electricity</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Account Status:</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                serviceData?.status === 'active'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
              }`}>
                {serviceData?.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Contact Support Note */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-3">
            <MapPin size={20} className="text-blue-600 dark:text-blue-400 mt-1" />
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-300 mb-1">Need to update your address?</h4>
              <p className="text-sm text-blue-800 dark:text-blue-400">
                Contact your building administrator or {serviceData?.organizationName} support to update your service address.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>;
};
export default Address;